/**
 * Single source of truth for the public app version and user-visible
 * changelog. Bump APP_VERSION and prepend a new CHANGELOG entry in the
 * SAME PR that ships a user-visible change.
 *
 * Versioning: semver.
 *   - Patch (0.2.0 → 0.2.1): bug fixes, telemetry additions, internal only
 *   - Minor (0.2.0 → 0.3.0): user-visible features, new integrations
 *   - Major (0.x.y → 1.x.y): breaking changes to config format or API surface
 *
 * Purely internal PRs (tests, refactors, CI) do not bump the version or add
 * an entry — call that out in the PR description so the pattern stays clean.
 */

export const APP_VERSION = '0.3.4' as const;

export type ChangelogEntry = {
  version: string;
  date: string; // ISO YYYY-MM-DD
  title: string;
  summary?: string;
  entries: string[];
};

/**
 * Reverse-chronological: newest first. The /changelog page renders this
 * list top-down and the header version chip reads APP_VERSION (which must
 * match CHANGELOG[0].version).
 */
export const CHANGELOG: readonly ChangelogEntry[] = [
  {
    version: '0.3.4',
    date: '2026-04-24',
    title: 'Production deploy + llms.txt fixes',
    summary:
      'Fixed two related infrastructure issues surfaced during the first push-to-production rollout — the client deploy was creating Preview-class Pages deployments that never reached the production domain, and /llms.txt was being swallowed by the SPA fallback.',
    entries: [
      'Fixed the dashboard deploy script so `npm run deploy:prod` targets the Cloudflare Pages production branch (previously it created Preview deployments that never became live).',
      'Added a /llms.txt handler to the Pages Functions middleware so AI crawlers get the real content instead of the SPA shell. The file is now generated dynamically from the same registry that powers the sitemap.',
      'Removed the hand-edited public/llms.txt and public/sitemap.xml — both are now generated from src/shared/public-seo.ts on every request so they never go stale.',
    ],
  },
  {
    version: '0.3.3',
    date: '2026-04-24',
    title: 'Zapier comparison page + nav and footer refresh',
    summary:
      'Shipped the second comparison page (FramerSync vs Zapier) and simplified the public chrome: the top nav is now just logo and auth, with the Features menu living as a grouped dropdown in the footer.',
    entries: [
      'New /framer-cms-sync-vs-zapier page covering where Zapier fits, where FramerSync fits, and how to use both together as a hybrid setup.',
      'Simplified the public top nav: removed Home, Features, and Changelog links so the header is just the logo plus the version chip and auth buttons.',
      'Moved the Features menu into the footer as a popup with three horizontal columns (Sources, Workflows, Compare), each item rendered as a single text link.',
    ],
  },
  {
    version: '0.3.2',
    date: '2026-04-24',
    title: 'Navigation refresh on the public site',
    summary:
      'The public marketing nav gets a Features dropdown grouped by Sources / Workflows / Compare, a direct Changelog link, and a persistent version chip next to the auth buttons.',
    entries: [
      'Added a Features dropdown surfacing every landing page grouped by intent (Sources, Workflows, Compare).',
      'Added a Changelog link to the public nav so visitors can discover product updates without digging into the footer.',
      'Added a clickable version chip next to Sign in / Create account, matching the in-app header chip — same destination, same styling.',
    ],
  },
  {
    version: '0.3.1',
    date: '2026-04-24',
    title: 'Honest comparison page for manual CSV import',
    summary:
      'Shipped a comparison page covering when manual CSV import vs FramerSync makes more sense, aimed at teams weighing the switch.',
    entries: [
      'New /framer-cms-sync-vs-manual-csv-import page laying out where CSV wins, where it hurts, and the switch-over point teams usually hit.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-04-24',
    title: 'Three new integration landing pages',
    summary:
      'Added dedicated pages for Notion, webhook-triggered syncs, and scheduled updates so teams can find the exact workflow they need without scanning the whole homepage.',
    entries: [
      'New /sync-notion-to-framer page covering OAuth connection, property-type-aware mapping, and common Notion workflows.',
      'New /framer-cms-webhook-sync page explaining signed HMAC triggers for event-driven syncs from Zapier, Make, or custom backends.',
      'New /scheduled-framer-cms-updates page covering interval and cron scheduling with run history for operational visibility.',
      'Repointed homepage integration card and feature cards to the new dedicated pages.',
    ],
  },
  {
    version: '0.2.1',
    date: '2026-04-24',
    title: 'Expanded homepage FAQ',
    summary:
      'Added eight new FAQ entries answering the questions we hear most often on sales calls and support. Each flows through the existing structured-data schema so Google can surface the answers as rich results.',
    entries: [
      'Covered sync cadence, rate-limit handling, nested JSON support, partial-failure safety, multi-collection source reuse, webhook triggers from external tools, data residency, and the one-way-only design.',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-04-24',
    title: 'Reliability + observability pass',
    summary:
      'Hardened the sync pipeline end-to-end, added deeper telemetry for failed runs, and fixed an Airtable edge case that could assign the wrong CMS id.',
    entries: [
      'Surfaced per-field mapping failures in run telemetry — you can now see which source field is driving partial syncs, not just that one happened.',
      'Enriched run-finished events with error category (auth, rate_limited, timeout, network, provider, etc.) and a sanitized error message.',
      'Rejected non-JSON responses with a clear error — previously these produced confusing parse failures mid-run.',
      'Added request and response size caps on webhook and test-API endpoints to prevent memory pressure from misbehaving upstreams.',
      'Fixed an Airtable edge case where a user column named "id" silently overrode the Airtable record id, producing inconsistent CMS identity. The user value is now preserved under _id for backward compatibility.',
      'Redesigned production deploy pipeline: production only deploys from a dedicated "production" branch, gated behind a typed confirmation token.',
      'Cleaned up hundreds of Tailwind deprecations across the dashboard (min-h-dvh, bg-linear, size-*).',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-04-10',
    title: 'Public beta',
    summary:
      'First public beta release. REST, Airtable, Google Sheets, and Notion sources syncing to Framer CMS with field mapping, scheduled automations, and webhook triggers.',
    entries: [
      'Initial public beta release.',
      'REST API, Airtable, Google Sheets, and Notion source integrations.',
      'Drag-and-drop field mapping with type inference.',
      'Scheduled (cron + interval) and webhook-triggered syncs.',
      'Live run history and progress visualization.',
    ],
  },
];

/**
 * Compact "latest release" for components that just need to show what just shipped.
 */
export function getLatestChangelogEntry(): ChangelogEntry {
  if (CHANGELOG.length === 0) {
    throw new Error('CHANGELOG is empty; add at least one entry');
  }
  return CHANGELOG[0];
}
